import React, { FC, useState } from 'react';
import styled, {css} from "src/theme";
import { usePresenter } from './usePresenter';
import { Row, Col, Carousel, Modal, Form, Input, Checkbox, message, Upload, Select } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { Button as AntButton } from 'antd';
import type { UploadFile } from 'antd/lib/upload/interface';
import InputMask from 'react-input-mask';
import useMediaQuery from "use-media-antd-query";
import { arrowRight } from 'src/assets/images';
import SVG from 'react-inlinesvg';
import { Button } from 'src/components/UI/Button';
import { submitContactForm } from 'src/api';
import { useStoreon } from 'storeon/react';
import { IEvents, IState } from 'src/store';

interface Props {}

const SampleNextArrowStyled = styled.div`
  &.slick-next {
    display: flex !important;
    align-items: center;
    height: 100%;
    right: 0;
    top: 0;
  }
`;

const slickSettings = {
  dots: false,
  infinite: true,
  slidesToShow: 3,
  arrows: true,
  draggable: false,
  nextArrow: <SampleNextArrowStyled><SVG src={arrowRight} /></SampleNextArrowStyled>,
}

export const Step1: FC<Props> = () => {
  const presenter = usePresenter();
  const { appStore } = useStoreon<IState, IEvents>('appStore');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const colSize = useMediaQuery();
  const isMobile = colSize === 'xs' || colSize === 'sm' || colSize === 'md';
  const isTablet = colSize === 'lg' || colSize === 'xl';

  const ALLOWED_EXTENSIONS = ['webp', 'heic', 'jpg', 'jpeg', 'pdf', 'png'];
  const MAX_FILE_SIZE_MB = 10;
  const MAX_FILES = 10;

  const handleBeforeUpload = (file: File, currentFileList: File[]) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      message.error('Допустимые форматы: webp, heic, jpg, jpeg, pdf, png.');
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message.error('Размер файла не должен превышать 10 МБ.');
      return Upload.LIST_IGNORE;
    }
    if (fileList.length + currentFileList.length > MAX_FILES) {
      message.error('Максимум 10 файлов.');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    const files = fileList
      .map((f) => f.originFileObj as File)
      .filter(Boolean);
    try {
      await submitContactForm({
        name: values.name,
        phone: values.phone,
        email: values.email,
        city_id: values.city_id,
        comment: values.comment || '',
        consent: values.consent,
        form: 'roof-multipitch',
        files: files.length > 0 ? files : undefined,
      });
      setSubmitted(true);
    } catch (e: any) {
      const errors = e?.response?.data?.errors;
      if (errors) {
        const fields = Object.keys(errors).map((key) => ({
          name: key,
          errors: errors[key],
        }));
        form.setFields(fields);
      } else {
        message.error('Произошла ошибка. Попробуйте позже.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    presenter.setMultipitchModalOpen(false);
    setSubmitted(false);
    setFileList([]);
    form.resetFields();
  };

  return (
    <>
      <Row justify="space-between">
        {!isTablet && presenter.roofCards.map((card) => (
            <Col span={isMobile ? 24 : 'auto'} key={card.id}>
              <ButtonStyled
                $active={card.type === presenter.activeRoof}
                onClick={() => presenter.chooseRoofType(card.type)}
              >
                <img src={card.img} alt={card.label}/>
              </ButtonStyled>
              <TitleStyled>{card.label}</TitleStyled>
            </Col>
          ))
        }
        {isTablet && (
          <Col span={isMobile ? 24 : 'auto'}>
            <Carousel style={{ padding: '0 50px' }} {...slickSettings}>
              {presenter.roofCards.map((card) => (
                  <div key={card.id}>
                    <ButtonStyled $active={card.type === presenter.activeRoof}
                                  onClick={() => presenter.chooseRoofType(card.type)}
                    >
                      <img src={card.img} alt={card.label}/>
                    </ButtonStyled>
                    <TitleStyled>{card.label}</TitleStyled>
                  </div>
              ))}
            </Carousel>
          </Col>
        )}
      </Row>

      <Modal
        title="Многоскатная кровля"
        centered
        visible={presenter.multipitchModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={500}
        destroyOnClose
      >
        {submitted ? (
          <SuccessMessage>
            <SuccessTitle>Заявка отправлена!</SuccessTitle>
            <SuccessText>
              Наш менеджер свяжется с вами в ближайшее время для обсуждения
              параметров многоскатной кровли и расчёта стоимости.
            </SuccessText>
            <Button onClick={handleModalClose}>Закрыть</Button>
          </SuccessMessage>
        ) : (
          <>
            <ModalDescription>
              Расчёт многоскатной кровли требует индивидуального подхода.
              Оставьте ваши контактные данные, и наш менеджер свяжется с вами
              для уточнения размеров и расчёта стоимости.
            </ModalDescription>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Form.Item
                name="name"
                label="Имя"
                rules={[{ required: true, message: 'Укажите имя' }]}
              >
                <Input placeholder="Ваше имя" />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Телефон"
                rules={[
                  { required: true, message: 'Укажите телефон' },
                  {
                    pattern: /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/,
                    message: 'Введите полный номер телефона',
                  },
                ]}
              >
                <InputMask mask="+7 (999) 999-99-99">
                  {/* @ts-ignore */}
                  {(inputProps: any) => <Input {...inputProps} placeholder="+7 (___) ___-__-__" />}
                </InputMask>
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Укажите email' },
                  { type: 'email', message: 'Укажите корректный email' },
                ]}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>

              <Form.Item
                name="city_id"
                label="Город"
                rules={[{ required: true, message: 'Выберите город' }]}
              >
                <Select
                  placeholder="Выберите город"
                  showSearch
                  optionFilterProp="label"
                  options={appStore.cities.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="comment"
                label="Комментарий"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Опишите вашу кровлю, пожелания или вопросы"
                />
              </Form.Item>

              <Form.Item label="Файлы">
                <Upload
                  beforeUpload={handleBeforeUpload}
                  fileList={fileList}
                  onChange={({ fileList: newFileList }) => setFileList(newFileList)}
                  multiple
                  accept=".webp,.heic,.jpg,.jpeg,.pdf,.png"
                >
                  {fileList.length >= MAX_FILES ? null : (
                    <AntButton icon={<UploadOutlined />}>Прикрепить файлы</AntButton>
                  )}
                </Upload>
              </Form.Item>

              <Form.Item
                name="consent"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject('Необходимо согласие на обработку данных'),
                  },
                ]}
              >
                <Checkbox
                  onChange={(e) => {
                    if (e.target.checked) {
                      form.setFields([{ name: 'consent', errors: [] }]);
                    }
                  }}
                >
                  Согласен на обработку персональных данных
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Button
                  onClick={() => form.submit()}
                  disabled={submitting}
                >
                  {submitting ? 'Отправка...' : 'Отправить заявку'}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </>
  );
};

const ButtonStyled = styled.button<{ $active: boolean }>`
  ${(props) => css`
    position: relative;
    display: block;
    padding: 0;
    margin: 0 auto;
    border: ${props.$active ? `2px solid ${props.theme.colors.orange}` : 'none'};
    border-radius: 24px;

    ${props.$active && `
      :after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 24px;
        background: #FFFA;
      }
    `}
  `}
`;

const TitleStyled = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0;
  text-transform: uppercase;
  font-family: Montserrat, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 17px;
  line-height: 21px;
  color: ${props => props.theme.colors.nero};
`;

const ModalDescription = styled.p`
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.6;
  color: #555;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const SuccessTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #52c41a;
  margin-bottom: 12px;
`;

const SuccessText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 24px;
`;
